/*
 * Main game loop — owns the player + critters + minigames + net client + world
 * clock + audio cue triggers + zone state, drives the scene each tick.
 *
 * Integrations folded in here:
 *  - TASK-06: net client wire-out for moves/chat/cosmetic/score, AI fallback population
 *  - TASK-08: stargaze minigame trigger + tick + tap-routing-to-hit-test
 *  - TASK-09: emote button + emote modal + wire send/receive + AI reactions
 *  - TASK-10: tickWorld each frame so the sky/lanterns/fireflies/weather breathe
 *  - TASK-13: audio cues at mushroom pickup, fish nibble/catch, unlock, chat, emote, portal
 *  - TASK-07: portal proximity prompt + zone-swap UX (visual-only zone change today;
 *             the "separate PartyKit room per zone" path is deferred to a follow-up)
 */

import { Application } from 'pixi.js';
import type { AnyCritter, NpcCritter, PlayerCritter } from '@/state/types';
import { WORLD } from '@/render/palette';
import { save, persist } from '@/state/save';
import { NPC_NAMES, NPC_PHRASES, PHRASES } from '@/content/phrases';
import { FUR_COLORS } from '@/content/furColors';
import { EMOTES, emoteByName } from '@/content/emotes';
import { createScene } from '@/render/scene';
import { initKeys, readMovement, setNibbleHandler } from '@/input/keys';
import { initPointer } from '@/input/pointer';
import {
  hideHint,
  scheduleHintAutoHide,
  updateStatus,
  updateWellPrompt,
  setNibbleActive,
} from '@/ui/hud';
import { openModal, closeModal, $ } from '@/ui/modal';
import { showUnlockToast } from '@/ui/toast';
import {
  startForage,
  tickForage,
  createForageState,
} from '@/minigames/forage';
import {
  startFish,
  tickFish,
  handleNibbleTap,
  createFishState,
} from '@/minigames/fish';
import {
  STARGAZE_DURATION_SEC,
  createStargazeState,
  startStargaze,
  tickStargaze,
  tryHitStar,
  wishForHit,
} from '@/minigames/stargaze';
import { checkUnlocks } from '@/state/unlocks';
import { createNetClient, type NetClient, type NetStatus } from '@/net/client';
import { applyRemotePosition, tickRemote } from '@/state/remote';
import { tickWorld } from '@/state/world';
import { sounds } from '@/audio/cues';
import {
  CHAT_BUBBLE_TTL_MS,
  EMOTE_TTL_MS,
  type EmoteName,
  type ServerMessage,
  type WireCritter,
} from '../../shared/protocol';
import {
  getCurrentZone,
  setCurrentZone,
  spawnPositionFor,
  portalFor,
  type ZoneId,
} from '@/state/zone';

const PLAYER_SPEED = 170;
const PLAYER_KEY_SPEED = 180;
const NPC_SPEED = 80;

const AI_NAME_SUFFIX = ' 🍂';
const AI_FALLBACK_COUNT = 2;
const AI_NAME_POOL: readonly string[] = NPC_NAMES;

/** Radius at which an AI critter notices and may react to the player's emote. */
const EMOTE_REACTION_RADIUS = 120;

export async function startGame(stage: HTMLElement): Promise<void> {
  // Boot PixiJS.
  const app = new Application();
  await app.init({
    width: WORLD.width,
    height: WORLD.height,
    background: 0x4a3b6b,
    antialias: true,
    autoStart: true,
    resolution: Math.max(1, window.devicePixelRatio || 1),
    autoDensity: true,
    resizeTo: stage,
  });
  stage.appendChild(app.canvas);

  const fit = (): void => {
    const cssW = stage.clientWidth || WORLD.width;
    const cssH = stage.clientHeight || WORLD.height;
    app.stage.scale.set(cssW / WORLD.width, cssH / WORLD.height);
  };
  fit();
  window.addEventListener('resize', fit);

  // Build scene.
  const scene = createScene();
  app.stage.addChild(scene.container);

  // Zone — restored from localStorage. Affects spawn position and which
  // proximity props are reachable.
  let currentZone: ZoneId = getCurrentZone();
  scene.setZone(currentZone);
  const initialSpawn = spawnPositionFor(currentZone);

  // Player critter.
  const player: PlayerCritter = {
    id: 'self',
    isPlayer: true,
    name: save.name,
    x: initialSpawn.x,
    y: initialSpawn.y,
    tx: initialSpawn.x,
    ty: initialSpawn.y,
    color: save.color,
    hat: save.hat,
    neck: save.neck,
    state: 'active',
    bubble: null,
    bubbleExpiresAt: 0,
    emote: null,
    emoteExpiresAt: 0,
  };

  const npcs = new Map<string, NpcCritter>();
  const aiNamePool = [...AI_NAME_POOL].sort(() => Math.random() - 0.5);
  let aiSpawnCounter = 0;

  function spawnAiNpc(): NpcCritter {
    const id = `ai-${aiSpawnCounter++}`;
    const baseName = aiNamePool[aiSpawnCounter % aiNamePool.length] ?? 'Sprout';
    return {
      id,
      isPlayer: false,
      kind: 'ai',
      name: baseName + AI_NAME_SUFFIX,
      x: 100 + Math.random() * 400,
      y: 250 + Math.random() * 400,
      tx: 100 + Math.random() * 400,
      ty: 250 + Math.random() * 400,
      color: Math.floor(Math.random() * FUR_COLORS.length),
      hat: Math.floor(Math.random() * 5),
      neck: 0,
      state: 'active',
      bubble: null,
      bubbleExpiresAt: 0,
      emote: null,
      emoteExpiresAt: 0,
      waitTime: Math.random() * 3 + 1,
    };
  }

  function spawnRemoteNpc(wire: WireCritter): NpcCritter {
    return {
      id: wire.id,
      isPlayer: false,
      kind: 'remote',
      name: wire.name,
      x: wire.x,
      y: wire.y,
      tx: wire.x,
      ty: wire.y,
      color: wire.color,
      hat: wire.hat,
      neck: 0,
      state: wire.state,
      bubble: null,
      bubbleExpiresAt: 0,
      emote: null,
      emoteExpiresAt: 0,
      waitTime: 0,
    };
  }

  function mountNpc(npc: NpcCritter): void {
    npcs.set(npc.id, npc);
    scene.addCritter(npc);
  }

  function unmountNpc(id: string): void {
    const n = npcs.get(id);
    if (!n) return;
    npcs.delete(id);
    scene.removeCritter(id);
  }

  function remoteCount(): number {
    let n = 0;
    for (const c of npcs.values()) if (c.kind === 'remote') n++;
    return n;
  }

  function aiCount(): number {
    let n = 0;
    for (const c of npcs.values()) if (c.kind === 'ai') n++;
    return n;
  }

  function ensureAiPopulation(): void {
    const target =
      remoteCount() === 0 && netStatus !== 'connecting' ? AI_FALLBACK_COUNT : 0;
    const current = aiCount();
    if (current < target) {
      for (let i = 0; i < target - current; i++) mountNpc(spawnAiNpc());
    } else if (current > target) {
      const aiIds = [...npcs.values()].filter((c) => c.kind === 'ai').map((c) => c.id);
      for (let i = 0; i < current - target; i++) {
        const id = aiIds[aiIds.length - 1 - i];
        if (id) unmountNpc(id);
      }
    }
  }

  scene.addCritter(player);

  // Minigame states.
  const forage = createForageState();
  const fish = createFishState();
  const stargaze = createStargazeState();

  initKeys();
  scheduleHintAutoHide();

  initPointer({
    target: stage,
    onMove: (wx, wy) => {
      // Tap during stargaze → hit-test instead of move.
      if (stargaze.active) {
        const hit = tryHitStar(stargaze, wx, wy, scene.stargaze.activeStars());
        if (hit) {
          scene.spawnFloatText(wishForHit(), hit.x, hit.y - 12, '#ffe9a8');
          sounds.mushroomPickup(); // soft chime — repurposed for star hit
          updateStatus(forage, fish, { active: stargaze.active, score: stargaze.score, timeLeft: stargaze.timeLeft });
        }
        return;
      }
      if (fish.active) return;
      player.tx = wx;
      player.ty = wy;
      hideHint();
    },
    shouldIgnore: () => fish.state === 'nibble' || fish.active,
  });

  setNibbleHandler(() => onNibbleTap());

  // HUD button wiring.
  $('customize-btn').addEventListener('click', () => {
    ($('name-input') as HTMLInputElement).value = save.name;
    $('hat-hint').textContent = '';
    openModal('customize-modal');
  });
  $('say-btn').addEventListener('click', () => openModal('say-modal'));
  $('emote-btn').addEventListener('click', () => openModal('emote-modal'));
  $('play-btn').addEventListener('click', () => {
    if (!forage.active && !fish.active && !stargaze.active) onStartForage();
  });
  $('well-prompt').addEventListener('click', () => {
    if (!forage.active && !fish.active && !stargaze.active) onStartFish();
  });
  $('bench-prompt').addEventListener('click', () => {
    if (!forage.active && !fish.active && !stargaze.active) onStartStargaze();
  });
  $('portal-prompt').addEventListener('click', () => {
    if (forage.active || fish.active || stargaze.active) return;
    travelThroughPortal();
  });

  $('nibble-tap').addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    onNibbleTap();
  });

  // Emote picker — built from the EMOTES catalog.
  buildEmotePicker();

  function buildEmotePicker(): void {
    const grid = $('emote-grid');
    grid.innerHTML = '';
    for (const emote of EMOTES) {
      const btn = document.createElement('button');
      btn.className = 'phrase-btn';
      btn.type = 'button';
      btn.textContent = emote.label;
      btn.addEventListener('click', () => {
        playLocalEmote(emote.name);
        closeModal('emote-modal');
      });
      grid.appendChild(btn);
    }
  }

  function playLocalEmote(name: EmoteName): void {
    const now = performance.now();
    player.emote = name;
    player.emoteExpiresAt = now + EMOTE_TTL_MS;
    // Local audio + tiny caption bubble for immediate feedback.
    if (name === 'wave') sounds.emoteWave();
    player.bubble = emoteByName(name).caption;
    player.bubbleExpiresAt = now + EMOTE_TTL_MS;
    net.sendEmote(name);
    // AI reactions — soft, optional.
    scheduleAiReactionsToPlayerEmote(name);
  }

  function scheduleAiReactionsToPlayerEmote(name: EmoteName): void {
    for (const npc of npcs.values()) {
      if (npc.kind !== 'ai') continue;
      const d = Math.hypot(npc.x - player.x, npc.y - player.y);
      if (d > EMOTE_REACTION_RADIUS) continue;
      const chance =
        name === 'wave' ? 0.6 : name === 'dance' ? 0.25 : 0;
      if (Math.random() >= chance) continue;
      const delayMs = 250 + Math.random() * 600;
      window.setTimeout(() => {
        const now = performance.now();
        npc.emote = name;
        npc.emoteExpiresAt = now + EMOTE_TTL_MS;
        npc.bubble = emoteByName(name).caption;
        npc.bubbleExpiresAt = now + EMOTE_TTL_MS;
      }, delayMs);
    }
  }

  function travelThroughPortal(): void {
    const portal = portalFor(currentZone);
    sounds.portalStep();
    currentZone = portal.toZone;
    setCurrentZone(currentZone);
    scene.setZone(currentZone);
    const sp = spawnPositionFor(currentZone);
    player.x = player.tx = sp.x;
    player.y = player.ty = sp.y;
    // Snap any active AI critters away from the old portal too so they look
    // like they belong in the new zone (cheap teleport).
    for (const npc of npcs.values()) {
      if (npc.kind !== 'ai') continue;
      const spawn = spawnAiNpc();
      npc.x = npc.tx = spawn.x;
      npc.y = npc.ty = spawn.y;
    }
  }

  // Welcome modal name pickup.
  $('welcome-go').addEventListener('click', () => {
    const v = ($('welcome-name') as HTMLInputElement).value.trim();
    if (v) player.name = v;
    closeModal('welcome-modal');
  });

  /* ──────────────────────────────────────────────────────────
   * Net client
   * ────────────────────────────────────────────────────────── */

  let netStatus: NetStatus = 'idle';
  const net: NetClient = createNetClient({
    initialCritter: { name: save.name, color: save.color, hat: save.hat },
    onServerMessage: (msg) => onServerMessage(msg),
    onStatusChange: (s) => {
      netStatus = s;
      ensureAiPopulation();
    },
  });

  ensureAiPopulation();

  function onServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'server_welcome':
        if (msg.you.name && msg.you.name !== player.name) {
          player.name = msg.you.name;
          save.name = msg.you.name;
          void persist();
        }
        return;
      case 'server_room_state':
        for (const c of [...npcs.values()]) {
          if (c.kind === 'remote') unmountNpc(c.id);
        }
        for (const w of msg.critters) {
          if (!npcs.has(w.id)) mountNpc(spawnRemoteNpc(w));
        }
        ensureAiPopulation();
        return;
      case 'server_critter_joined':
        if (!npcs.has(msg.critter.id)) {
          mountNpc(spawnRemoteNpc(msg.critter));
          ensureAiPopulation();
        }
        return;
      case 'server_critter_left':
        unmountNpc(msg.id);
        ensureAiPopulation();
        return;
      case 'server_critter_moved': {
        const c = npcs.get(msg.id);
        if (c && c.kind === 'remote') applyRemotePosition(c, msg.x, msg.y);
        return;
      }
      case 'server_chat': {
        const c = npcs.get(msg.id);
        if (!c) return;
        const phrase = PHRASES[msg.phrase];
        if (!phrase) return;
        c.bubble = phrase;
        c.bubbleExpiresAt = msg.t + CHAT_BUBBLE_TTL_MS;
        sounds.chatPop();
        return;
      }
      case 'server_cosmetic_changed': {
        const c = npcs.get(msg.id);
        if (!c) return;
        c.color = msg.color;
        c.hat = msg.hat;
        return;
      }
      case 'server_critter_state_changed': {
        const c = npcs.get(msg.id);
        if (!c) return;
        c.state = msg.state;
        return;
      }
      case 'server_emote': {
        const c = npcs.get(msg.id);
        if (!c) return;
        c.emote = msg.emote;
        c.emoteExpiresAt = msg.t + EMOTE_TTL_MS;
        c.bubble = emoteByName(msg.emote).caption;
        c.bubbleExpiresAt = msg.t + EMOTE_TTL_MS;
        if (msg.emote === 'wave') sounds.emoteWave();
        return;
      }
      case 'server_leaderboard_update':
        return;
      case 'server_pong':
      case 'server_error':
        return;
      default:
        return;
    }
  }

  /* ──────────────────────────────────────────────────────────
   * Minigame triggers
   * ────────────────────────────────────────────────────────── */

  function onStartForage(): void {
    startForage(forage);
    const btn = $('play-btn') as HTMLButtonElement;
    btn.textContent = '...';
    btn.disabled = true;
    updateStatus(forage, fish, null);
  }

  function onForageEnded(score: number): void {
    const btn = $('play-btn') as HTMLButtonElement;
    btn.innerHTML = '🍄 Forage';
    btn.disabled = false;
    save.lifetimeForage += score;
    void persist();
    net.sendScore('forage', score);
    $('forage-end-score').textContent = String(score);
    const isBest = score >= save.bestForage;
    const bestEl = $('forage-end-best');
    bestEl.textContent = isBest
      ? `✨ New best! ${save.bestForage}`
      : `Best so far: ${save.bestForage}`;
    bestEl.classList.toggle('new', isBest);
    const newly = checkUnlocks({ forageScore: score, fishScore: 0 });
    if (newly.length) {
      showUnlockToast(newly);
      sounds.unlock();
    }
    updateStatus(forage, fish, null);
    openModal('forage-end-modal');
  }

  function onStartFish(): void {
    startFish(fish);
    player.tx = player.x = WORLD.wellX - 38;
    player.ty = player.y = WORLD.wellY + 5;
    updateStatus(forage, fish, null);
  }

  function onFishEnded(score: number): void {
    setNibbleActive(false);
    save.lifetimeFish += score;
    void persist();
    net.sendScore('fish', score);
    $('fish-end-score').textContent = String(score);
    const isBest = score >= save.bestFish;
    const bestEl = $('fish-end-best');
    bestEl.textContent = isBest
      ? `✨ New best! ${save.bestFish}`
      : `Best so far: ${save.bestFish}`;
    bestEl.classList.toggle('new', isBest);
    const newly = checkUnlocks({ forageScore: 0, fishScore: score });
    if (newly.length) {
      showUnlockToast(newly);
      sounds.unlock();
    }
    updateStatus(forage, fish, null);
    openModal('fish-end-modal');
  }

  function onNibbleTap(): void {
    if (fish.state !== 'nibble') return;
    setNibbleActive(false);
    const value = handleNibbleTap(fish);
    if (value > 0) {
      scene.spawnFloatText(`+${value} fish ✨`, WORLD.wellX, WORLD.wellY - 55, '#6bb0c0');
      sounds.fishCatch();
      updateStatus(forage, fish, null);
    }
  }

  function onStartStargaze(): void {
    // Anchor player at the bench.
    player.tx = player.x = 470;
    player.ty = player.y = 478;
    startStargaze(stargaze);
    updateStatus(forage, fish, { active: true, score: 0, timeLeft: STARGAZE_DURATION_SEC });
  }

  function onStargazeEnded(score: number): void {
    scene.stargaze.clear();
    $('stargaze-end-score').textContent = String(score);
    $('stargaze-end-title').textContent = score === 0 ? 'Quiet sky tonight.' : 'Lovely stars!';
    const isBest = score >= stargaze.best;
    const bestEl = $('stargaze-end-best');
    bestEl.textContent = isBest
      ? `✨ New best! ${stargaze.best}`
      : `Best so far: ${stargaze.best}`;
    bestEl.classList.toggle('new', isBest);
    const newly = checkUnlocks({ forageScore: 0, fishScore: 0, stargazeScore: score });
    if (newly.length) {
      showUnlockToast(newly);
      sounds.unlock();
    }
    updateStatus(forage, fish, null);
    openModal('stargaze-end-modal');
  }

  /* ──────────────────────────────────────────────────────────
   * Say-phrase: local bubble + chatCount + wire send + sound
   * ────────────────────────────────────────────────────────── */

  installSayPhrase((phrase) => {
    player.bubble = phrase;
    player.bubbleExpiresAt = performance.now() + 3000;
    save.chatCount += 1;
    void persist();
    sounds.chatPop();
    const newly = checkUnlocks({ forageScore: 0, fishScore: 0 });
    if (newly.length) {
      showUnlockToast(newly);
      sounds.unlock();
    }
    const idx = PHRASES.indexOf(phrase);
    if (idx >= 0) net.sendChat(idx);
    closeModal('say-modal');
  });

  /* ──────────────────────────────────────────────────────────
   * Per-tick housekeeping
   * ────────────────────────────────────────────────────────── */

  let lastSentColor = save.color;
  let lastSentHat = save.hat;
  let lastForageScore = forage.score;
  let lastFishScore = fish.score;

  function syncPlayerFromSave(): void {
    if (player.color !== save.color) player.color = save.color;
    if (player.hat !== save.hat) player.hat = save.hat;
    if (player.neck !== save.neck) player.neck = save.neck;
    if (player.name !== save.name) player.name = save.name;
    if (save.color !== lastSentColor || save.hat !== lastSentHat) {
      lastSentColor = save.color;
      lastSentHat = save.hat;
      net.sendCosmetic(save.color, save.hat);
    }
  }

  let secsSinceTimeFlush = 0;
  let secsSinceLazyUnlockCheck = 0;

  /* ──────────────────────────────────────────────────────────
   * Main loop
   * ────────────────────────────────────────────────────────── */

  app.ticker.add((ticker) => {
    const dt = Math.min(ticker.deltaMS / 1000, 0.05);
    const t = performance.now();

    // Advance the world clock first so render modules pulling
    // currentWorldState() see fresh data this frame.
    tickWorld(dt);

    syncPlayerFromSave();

    save.playSeconds += dt;
    secsSinceTimeFlush += dt;
    if (secsSinceTimeFlush >= 30) {
      secsSinceTimeFlush = 0;
      void persist();
    }
    secsSinceLazyUnlockCheck += dt;
    if (secsSinceLazyUnlockCheck >= 1) {
      secsSinceLazyUnlockCheck = 0;
      const newly = checkUnlocks({ forageScore: 0, fishScore: 0 });
      if (newly.length) {
        showUnlockToast(newly);
        sounds.unlock();
      }
    }

    // Player movement (locked during fish + stargaze).
    if (!fish.active && !stargaze.active) {
      const m = readMovement();
      if (m.dx !== 0 || m.dy !== 0) {
        player.tx += m.dx * PLAYER_KEY_SPEED * dt;
        player.ty += m.dy * PLAYER_KEY_SPEED * dt;
        player.tx = Math.max(40, Math.min(WORLD.width - 40, player.tx));
        player.ty = Math.max(150, Math.min(WORLD.height - 80, player.ty));
        hideHint();
      }
      moveToward(player, PLAYER_SPEED, dt);
    }

    // Push position to wire.
    net.sendMove(player.x, player.y);

    // NPCs.
    for (const npc of npcs.values()) {
      if (npc.kind === 'ai') {
        moveToward(npc, NPC_SPEED, dt);
        npc.waitTime -= dt;
        if (npc.waitTime <= 0) {
          npc.tx = Math.max(70, Math.min(WORLD.width - 70, npc.x + (Math.random() - 0.5) * 220));
          npc.ty = Math.max(180, Math.min(WORLD.height - 120, npc.y + (Math.random() - 0.5) * 220));
          npc.waitTime = 2.5 + Math.random() * 4;
          if (Math.random() < 0.32) {
            const phrase = NPC_PHRASES[Math.floor(Math.random() * NPC_PHRASES.length)] ?? '✨';
            npc.bubble = phrase;
            npc.bubbleExpiresAt = performance.now() + 2600;
          }
        }
        // Clear stale emote (and its caption bubble).
        if (npc.emote && npc.emoteExpiresAt && npc.emoteExpiresAt < t) {
          npc.emote = null;
        }
      } else {
        tickRemote(npc, t);
        if (npc.bubble && npc.bubbleExpiresAt < t) npc.bubble = null;
        if (npc.emote && npc.emoteExpiresAt && npc.emoteExpiresAt < t) npc.emote = null;
      }
    }

    // Player emote auto-clear.
    if (player.emote && player.emoteExpiresAt && player.emoteExpiresAt < t) {
      player.emote = null;
    }

    // Minigames.
    if (forage.active) {
      const ended = tickForage(forage, dt, player);
      if (forage.score > lastForageScore) {
        sounds.mushroomPickup();
        lastForageScore = forage.score;
      }
      if (ended) {
        onForageEnded(forage.score);
        lastForageScore = 0;
      } else {
        updateStatus(forage, fish, null);
      }
    }
    if (fish.active) {
      tickFish(fish, dt, {
        onNibbleStart: () => {
          setNibbleActive(true);
          sounds.fishNibble();
        },
        onNibbleMiss: () => {
          setNibbleActive(false);
          scene.spawnFloatText('it got away…', WORLD.wellX, WORLD.wellY - 55, '#aa8a78');
        },
        onEnd: (score) => onFishEnded(score),
      });
      if (fish.score > lastFishScore) lastFishScore = fish.score; // (sound already fires on tap)
      updateStatus(forage, fish, null);
    }
    if (stargaze.active) {
      tickStargaze(stargaze, dt, {
        spawnStar: () => scene.stargaze.spawn(),
        onSpawn: () => {
          /* spawn was already handled by spawnStar */
        },
        onHit: () => {
          /* hit is processed on tap; this only fires if tickStargaze ever
             chose to auto-hit, which it doesn't */
        },
        onEnd: (score) => onStargazeEnded(score),
      });
      updateStatus(forage, fish, {
        active: stargaze.active,
        score: stargaze.score,
        timeLeft: stargaze.timeLeft,
      });
    }

    // Proximity prompts — square-only props.
    if (currentZone === 'square') {
      const distWell = Math.hypot(player.x - WORLD.wellX, player.y - WORLD.wellY);
      const distBench = Math.hypot(player.x - 475, player.y - 470);
      updateWellPrompt(stage, !forage.active && !fish.active && !stargaze.active && distWell < 80);
      updateProximityPrompt(stage, 'bench-prompt', 475, 405, !forage.active && !fish.active && !stargaze.active && distBench < 70);
    } else {
      updateWellPrompt(stage, false);
      updateProximityPrompt(stage, 'bench-prompt', 0, 0, false);
    }

    // Portal prompt — both zones.
    const portal = portalFor(currentZone);
    const distPortal = Math.hypot(player.x - portal.x, player.y - portal.y);
    const portalEl = $('portal-prompt');
    portalEl.textContent = portal.label;
    updateProximityPrompt(stage, 'portal-prompt', portal.x, portal.y - 50,
      !forage.active && !fish.active && !stargaze.active && distPortal < portal.radius);

    // Update scene snapshot.
    const critters: AnyCritter[] = [player, ...npcs.values()];
    scene.update(t, dt, {
      critters,
      forageMushrooms: forage.active ? forage.mushrooms : [],
      fish,
    });
  });

  return;
}

/* === helpers === */

function moveToward(c: AnyCritter, speed: number, dt: number): void {
  const dx = c.tx - c.x;
  const dy = c.ty - c.y;
  const d = Math.hypot(dx, dy);
  if (d > 0.5) {
    const step = Math.min(d, speed * dt);
    c.x += (dx / d) * step;
    c.y += (dy / d) * step;
  }
  if (c.bubble && c.bubbleExpiresAt < performance.now()) {
    c.bubble = null;
  }
}

/**
 * Generic proximity prompt placement — mirrors what `updateWellPrompt` does for
 * the well, but accepts arbitrary world coords + element id so we can reuse it
 * for the bench, portal, and future props.
 */
function updateProximityPrompt(
  stage: HTMLElement,
  elementId: string,
  worldX: number,
  worldY: number,
  visible: boolean
): void {
  const el = document.getElementById(elementId);
  if (!el) return;
  if (visible) {
    el.classList.add('visible');
    const rect = stage.getBoundingClientRect();
    const sx = rect.width / WORLD.width;
    const sy = rect.height / WORLD.height;
    el.style.left = `${worldX * sx}px`;
    el.style.top = `${worldY * sy}px`;
  } else {
    el.classList.remove('visible');
  }
}

/* ──────────────────────────────────────────────────────────
 * Say-phrase channel (unchanged from TASK-06)
 * ────────────────────────────────────────────────────────── */
type PhraseHandler = (phrase: string) => void;
let sayPhraseHandler: PhraseHandler | null = null;

export function installSayPhrase(handler: PhraseHandler): void {
  sayPhraseHandler = handler;
}

export function emitSayPhrase(phrase: string): void {
  sayPhraseHandler?.(phrase);
}
